import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { LayoutGrid, PlusCircle, Store, LogOut, Receipt, Sparkles, ListChecks, Plane, ClipboardCheck, Route, Package, Clapperboard, Factory, Gem } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DoneAlerts } from "./DoneAlerts";

const links = [
  { to: "/register", label: "Registers", icon: ListChecks, id: "nav-register" },
  "sep",
  { to: "/", label: "Kits", icon: LayoutGrid, id: "nav-dashboard" },
  { to: "/new", label: "New Kit", icon: PlusCircle, id: "nav-new-kit" },
  { to: "/production", label: "Production", icon: Factory, id: "nav-production" },
  "sep",
  { to: "/bespoke", label: "Bespoke Requests", icon: Sparkles, id: "nav-bespoke" },
  { to: "/bespoke-kits", label: "Bespoke Kits", icon: Gem, id: "nav-bespoke-kits" },
  "sep",
  { to: "/extras?type=kit", label: "Other (kits)", icon: Plane, id: "nav-extras-kit" },
  { to: "/extras?type=checklist", label: "Checklists", icon: ClipboardCheck, id: "nav-extras-checklist" },
  { to: "/extras?type=itinerary", label: "Itineraries", icon: Route, id: "nav-extras-itinerary" },
  "sep",
  { to: "/extras?type=bundle", label: "Bundles", icon: Package, id: "nav-extras-bundle" },
  "sep",
  { to: "/video-prompts", label: "Video Prompts", icon: Clapperboard, id: "nav-video-prompts" },
  "sep",
  { to: "/orders", label: "Orders & Delivery", icon: Receipt, id: "nav-orders" },
  { to: "/catalog", label: "Public Catalogue", icon: Store, id: "nav-catalog" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [att, setAtt] = useState(null);
  useEffect(() => {
    const get = () => http.get("/ops/attention").then((r) => setAtt(r.data)).catch(() => {});
    get();
    const t = setInterval(get, 30000);
    return () => clearInterval(t);
  }, [loc.pathname]);
  const active = (to, isActive) => {
    const [path, qs] = to.split("?");
    if (path === "/register") return loc.pathname === "/register";
    return qs ? loc.pathname === path && loc.search === `?${qs}` : isActive;
  };
  return (
    <div className="grain min-h-screen flex">
      <DoneAlerts />
      <aside className="w-64 shrink-0 border-r border-stone-200 bg-[#F7F5F0] sticky top-0 h-screen flex flex-col p-6 z-10">
        <div className="mb-6" data-testid="sidebar-brand">
          <img src="/logo-full.png" alt="PB Travel Angel" data-testid="sidebar-logo" className="w-40 h-auto" />
          <div className="eyebrow mt-3">Kit Studio</div>
          <div className="text-xs text-stone-500 mt-1">Book. Pack. Go.</div>
        </div>
        <nav className="flex flex-col gap-1 overflow-y-auto">
          {links.map((l, i) => l === "sep" ? <div key={`sep-${i}`} className="my-1.5 border-t border-stone-200" /> : (({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={id}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  active(to, isActive) ? "bg-[#C9A847]/12 text-[#9A7B2C]" : "text-stone-500 hover:text-stone-900 hover:bg-stone-100"
                }`
              }
            >
              <Icon size={16} /> {label}
              {id === "nav-dashboard" && att?.total > 0 && (
                <span data-testid="nav-dashboard-badge" title={`${att.total} thing(s) need your attention`}
                  className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white tabular-nums">{att.total}</span>
              )}
            </NavLink>
          ))(l))}
        </nav>
        <div className="mt-auto pt-6 border-t border-stone-200">
          <div className="text-xs text-stone-500 truncate" data-testid="current-user-email">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-3 flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 relative z-[1]">
        <Outlet />
      </main>
    </div>
  );
}
